import React, { useState, useMemo, useEffect, useRef } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { X, Minus, Plus, Heart } from "lucide-react";
import api, { money } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { MealDealPicker } from "@/components/MealDealPicker";
import { toast } from "sonner";

export default function ProductModal({ product, open, onClose, onFavourite, isFav, offerId = null, offerPrice = null, offerTitle = "" }) {
  const { addItem, selectedBranch } = useCart();
  const { settings } = useSettings();
  const [current, setCurrent] = useState(product);
  const [mealChoiceOpen, setMealChoiceOpen] = useState(false);
  const [mealPickerOpen, setMealPickerOpen] = useState(false);
  const [qty, setQty] = useState(1);
  const [selected, setSelected] = useState({});
  const [instructions, setInstructions] = useState("");
  const [recommended, setRecommended] = useState([]);
  const scrollRef = useRef(null);

  const reset = () => { setQty(1); setSelected({}); setInstructions(""); if (scrollRef.current) scrollRef.current.scrollTop = 0; };

  useEffect(() => { if (product) { setCurrent(product); reset(); } }, [product]);

  useEffect(() => {
    if (!open || !current?.id) { return; }
    const q = new URLSearchParams();
    if (selectedBranch) q.set("branch_id", selectedBranch.id);
    api.get(`/menu/products/${current.id}/recommendations?${q.toString()}`).then(({ data }) => setRecommended(data)).catch(() => setRecommended([]));
  }, [open, selectedBranch, current]);

  const groups = current?.modifier_groups || [];

  const toggle = (group, option) => {
    setSelected((prev) => {
      const cur = prev[group.id] || [];
      if (group.type === "single") return { ...prev, [group.id]: [option] };
      const exists = cur.find((o) => o.id === option.id);
      if (exists) return { ...prev, [group.id]: cur.filter((o) => o.id !== option.id) };
      if (group.max && cur.length >= group.max) return prev;
      return { ...prev, [group.id]: [...cur, option] };
    });
  };

  const chosen = useMemo(() => Object.values(selected).flat(), [selected]);
  const inOffer = !!offerId && offerPrice != null && current?.id === product?.id;
  const basePrice = inOffer ? Number(offerPrice) : (current?.price || 0);
  const unit = useMemo(
    () => basePrice + chosen.reduce((s, o) => s + (o.price || 0), 0),
    [basePrice, chosen]
  );
  const total = unit * qty;

  const missingRequired = groups.filter((g) => g.required && !(selected[g.id]?.length));

  const mealCfg = settings?.meal_deal || {};
  const mealEnabled = mealCfg.enabled !== false;
  const mainCats = mealCfg.main_categories || ["Burgers", "Chicken", "Kebabs", "Wraps", "Pizza"];
  const mainProducts = mealCfg.main_products || [];
  const defaultExtra = mealCfg.extra ?? 1.5;
  // Per-item overrides: which meal extra amount + which customization categories apply to this product.
  const itemCfg = (mealCfg.item_config || {})[current?.id] || {};
  const extrasList = mealCfg.extras || [];
  const chosenExtra = (() => {
    if (itemCfg.extra_id) {
      const e = extrasList.find((x) => x.id === itemCfg.extra_id);
      if (e) return { id: e.id, name: e.name || "Meal", amount: Number(e.amount) || 0 };
    }
    return { id: "meal-extra", name: "Meal", amount: defaultExtra };
  })();
  const allMealCats = mealCfg.customizations || [];
  const itemMealCats = (itemCfg.customization_ids && itemCfg.customization_ids.length)
    ? allMealCats.filter((c) => itemCfg.customization_ids.includes(c.id))
    : allMealCats;
  const isMealEligible = mealEnabled && !!current && (mainCats.includes(current.category_name) || mainProducts.includes(current.id)) && itemMealCats.some((cat) => (cat.items || []).some((it) => it.visible !== false && (it.name || "").trim()));

  const addMainToBasket = () => {
    const opts = chosen.map((o) => ({ id: o.id, name: o.name, price: o.price, group: o.group }));
    addItem({
      product_id: current.id, name: current.name, image: current.image,
      price: inOffer ? Number(offerPrice) : current.price, quantity: qty, selected_options: opts,
      special_instructions: instructions,
      ...(inOffer ? { offer_id: offerId, offer_title: offerTitle } : {}),
    });
    toast.success(`${current.name} added to basket`);
    onClose();
  };

  const handleAdd = () => {
    if (missingRequired.length) {
      toast.error(`Please choose ${missingRequired[0].name}`);
      return;
    }
    if (isMealEligible) { setMealChoiceOpen(true); return; }
    addMainToBasket();
  };

  const chooseMeal = () => { setMealChoiceOpen(false); setMealPickerOpen(true); };
  const addMealWithSides = (addons) => {
    const opts = chosen.map((o) => ({ id: o.id, name: o.name, price: o.price, group: o.group }));
    addItem({
      product_id: current.id, name: current.name, image: current.image,
      price: inOffer ? Number(offerPrice) : current.price, quantity: qty, selected_options: opts,
      meal_addons: [
        ...(chosenExtra.amount > 0 ? [{ id: chosenExtra.id, name: chosenExtra.name, price: chosenExtra.amount }] : []),
        ...(addons || []).map((a) => ({ id: a.id, name: a.name, price: a.price || 0 })),
      ],
      special_instructions: instructions,
      ...(inOffer ? { offer_id: offerId, offer_title: offerTitle } : {}),
    });
    toast.success(`${current.name} added to basket`);
    onClose();
  };

  const openRecommended = (p) => { setCurrent(p); reset(); };

  const quickAdd = (e, p) => {
    e.stopPropagation();
    const hasRequired = (p.modifier_groups || []).some((g) => g.required);
    if (hasRequired) { openRecommended(p); return; }
    addItem({ product_id: p.id, name: p.name, image: p.image, price: p.price, quantity: 1, selected_options: [], special_instructions: "" });
    toast.success(`${p.name} added to basket`);
  };

  if (!current) return null;

  const recs = recommended.filter((p) => p.id !== current.id).slice(0, 8);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="p-0 gap-0 max-w-lg w-[95vw] max-h-[92vh] overflow-hidden rounded-3xl bg-white border-0" data-testid="product-modal">
        <VisuallyHidden><DialogTitle>{current.name}</DialogTitle></VisuallyHidden>
        <div className="relative h-56 w-full bg-gray-100 shrink-0">
          {current.image ? <img src={current.image} alt={current.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gray-100" />}
          <button onClick={onClose} data-testid="modal-close"
            className="absolute top-3 right-3 bg-white/90 hover:bg-white rounded-full p-2 shadow">
            <X size={18} />
          </button>
          {onFavourite && (
            <button onClick={() => onFavourite(current)} data-testid="modal-fav"
              className="absolute top-3 left-3 bg-white/90 hover:bg-white rounded-full p-2 shadow">
              <Heart size={18} className={isFav ? "fill-metros-red text-metros-red" : ""} />
            </button>
          )}
        </div>
        <div ref={scrollRef} className="overflow-y-auto px-5 py-4" style={{ maxHeight: "calc(92vh - 224px - 84px)" }}>
          <h2 className="font-heading font-extrabold text-2xl text-metros-black">{current.name}</h2>
          <p className="text-sm text-muted-foreground mt-1">{current.description}</p>
          {current.allergens?.length > 0 && (
            <p className="text-xs text-amber-700 mt-2">Allergens: {current.allergens.join(", ")}</p>
          )}

          {groups.map((g) => (
            <div key={g.id} className="mt-5">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-heading font-bold text-metros-black">
                  {g.name} {g.required && <span className="text-metros-red">*</span>}
                </h4>
                <span className="text-xs text-muted-foreground">
                  {g.type === "single" ? "Choose 1" : g.max ? `Up to ${g.max}` : "Optional"}
                </span>
              </div>
              <div className="flex flex-col gap-2">
                {(g.options || []).map((o) => {
                  const active = (selected[g.id] || []).some((x) => x.id === o.id);
                  return (
                    <button key={o.id} data-testid={`option-${o.id}`}
                      onClick={() => toggle(g, { ...o, group: g.name })}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-colors ${active ? "border-metros-red bg-red-50" : "border-gray-200 hover:border-gray-300"}`}>
                      <span className="flex items-center gap-2.5">
                        <span className={`h-4 w-4 rounded-${g.type === "single" ? "full" : "md"} border flex items-center justify-center ${active ? "border-metros-red bg-metros-red" : "border-gray-300"}`}>
                          {active && <span className="h-1.5 w-1.5 bg-white rounded-full" />}
                        </span>
                        <span className="text-sm font-medium text-metros-black">{o.name}</span>
                      </span>
                      {o.price > 0 && <span className="text-sm font-semibold text-metros-red">+{money(o.price)}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="mt-5">
            <h4 className="font-heading font-bold text-metros-black mb-2">Special Instructions</h4>
            <textarea data-testid="special-instructions" value={instructions}
              onChange={(e) => setInstructions(e.target.value)}
              placeholder="Add any special instructions…"
              className="w-full rounded-xl border border-gray-200 p-3 text-sm resize-none h-20 focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </div>

          {recs.length > 0 && (
            <div className="mt-6" data-testid="recommended-section">
              <h4 className="font-heading font-bold text-metros-black mb-3">You might also like</h4>
              <div className="flex gap-3 overflow-x-auto no-scrollbar -mx-1 px-1 pb-1">
                {recs.map((p) => {
                  const hasRequired = (p.modifier_groups || []).some((g) => g.required);
                  return (
                    <div key={p.id} data-testid={`recommended-${p.id}`} onClick={() => openRecommended(p)}
                      className="shrink-0 w-32 text-left rounded-2xl border border-gray-200 hover:border-metros-red overflow-hidden transition-colors cursor-pointer relative group">
                      <div className="h-20 w-full bg-gray-100">
                        {p.image ? <img src={p.image} alt={p.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gray-100" />}
                      </div>
                      <button data-testid={`rec-add-${p.id}`} onClick={(e) => quickAdd(e, p)}
                        title={hasRequired ? "Choose options" : "Add to basket"}
                        className="absolute top-2 right-2 h-7 w-7 rounded-full bg-metros-red text-white flex items-center justify-center shadow active:scale-90 transition-transform">
                        <Plus size={16} />
                      </button>
                      <div className="p-2">
                        <p className="text-xs font-semibold text-metros-black leading-tight line-clamp-2">{p.name}</p>
                        <p className="text-xs font-bold text-metros-red mt-1">{money(p.price)}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-gray-100 p-4 flex items-center gap-3 shrink-0 bg-white">
          <div className="flex items-center gap-3 bg-gray-100 rounded-full px-2 py-1.5">
            <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1.5 hover:text-metros-red"><Minus size={16} /></button>
            <span className="font-bold w-5 text-center" data-testid="modal-qty">{qty}</span>
            <button data-testid="qty-plus" onClick={() => setQty((q) => q + 1)} className="p-1.5 hover:text-metros-red"><Plus size={16} /></button>
          </div>
          <button data-testid="add-to-basket-btn" onClick={handleAdd} disabled={missingRequired.length > 0}
            className="flex-1 bg-metros-red hover:bg-metros-redhover text-white rounded-full px-6 py-3.5 font-bold flex justify-center items-center gap-2 active:scale-95 transition-all disabled:opacity-50">
            <span>{missingRequired.length > 0 ? `Choose ${missingRequired[0].name}` : "Add to Basket"}</span>
            {missingRequired.length === 0 && <span>{money(total)}</span>}
          </button>
        </div>
      </DialogContent>

      <Dialog open={mealChoiceOpen} onOpenChange={setMealChoiceOpen}>
        <DialogContent className="z-[120] max-w-md w-[92vw] bg-white rounded-3xl" data-testid="meal-choice">
          <VisuallyHidden><DialogTitle>Choose how to add {current.name}</DialogTitle></VisuallyHidden>
          <div className="grid grid-cols-2 gap-3">
            <button data-testid="meal-choice-item" onClick={() => { setMealChoiceOpen(false); addMainToBasket(); }} className="group text-center focus:outline-none">
              <div className="rounded-2xl overflow-hidden border-2 border-gray-200 group-hover:border-metros-black aspect-square bg-gray-100 transition-colors">
                {current.image ? <img src={current.image} alt={current.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gray-100" />}
              </div>
              <p className="font-heading font-extrabold text-metros-black mt-2 leading-tight">Just the item</p>
              <p className="text-xs text-muted-foreground">{money(total)}</p>
            </button>
            <button data-testid="meal-choice-meal" onClick={chooseMeal} className="group text-center focus:outline-none">
              <div className="rounded-2xl overflow-hidden border-2 border-metros-red aspect-square bg-gray-100 group-hover:brightness-95 transition-all">
                {current.image ? <img src={current.image} alt={current.name} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gray-100" />}
              </div>
              <p className="font-heading font-extrabold text-metros-red mt-2 leading-tight">Make it a meal</p>
              {chosenExtra.amount > 0 && <p className="text-xs text-muted-foreground">+{money(chosenExtra.amount)}</p>}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <MealDealPicker open={mealPickerOpen} onOpenChange={setMealPickerOpen} onAdd={addMealWithSides} categories={itemMealCats} />
    </Dialog>
  );
}
